import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/StudentForm.css';

// Field configurations for different types of form fields
const getFieldConfig = (columnName) => {
  const config = {
    type: 'text',
    label: columnName
  };

  // Special handling for specific fields
  switch (columnName) {
    case 'Name':
      return { ...config, type: 'text', required: true, placeholder: 'Enter full name' };
    case 'Rno':
      return { 
        ...config, 
        type: 'number', 
        required: true, 
        placeholder: 'Enter roll number',
        min: '1',
        step: '1'
      };
    case 'Class':
      return {
        ...config,
        type: 'select',
        required: true,
        options: ['', 'NUR', 'KG1', 'KG2', ...Array.from({length: 12}, (_, i) => `STD${i+1}`)]
      };
    case 'Gender':
      return {
        ...config,
        type: 'select',
        options: ['', 'Male', 'Female']
      };
    case 'Mobile1':
    case 'Mobile2':
      return { ...config, type: 'tel', required: columnName === 'Mobile1' };
    case 'Address':
      return { ...config, type: 'textarea' };
    case 'Active':
      return { ...config, type: 'checkbox' };
    case 'Transport':
      return { ...config, type: 'checkbox' };
    case 'Default Discount':
      return { ...config, type: 'number', placeholder: 'Enter discount amount', min: '0', step: '1' };
    default:
      return config;
  }
};

// Simple function to filter out the Active field
const getVisibleFields = (columns) => {
  return columns.filter(column => column !== 'Active');
};

// Fields that must contain only numbers
const NUMERIC_FIELDS = ['FeeDues', 'Aadhar No', 'Pen No', 'Mobile1', 'Mobile2', 'Default Discount'];
const SIGNED_NUMERIC_FIELDS = ['FeeDues'];
const MOBILE_FIELDS = ['Mobile1', 'Mobile2'];
// Fields that must be valid dates (dd/mm/yy or dd/mm/yyyy)
const DATE_FIELDS = ['DOB', 'Date of Admission'];

const validateField = (name, value) => {
  if (!value || String(value).trim() === '') return ''; // empty is ok (not required)

  if (NUMERIC_FIELDS.includes(name)) {
    if (SIGNED_NUMERIC_FIELDS.includes(name)) {
      if (!/^-?\d+$/.test(String(value).trim())) {
        return 'Only numbers are allowed (negative allowed)';
      }
    } else if (!/^\d+$/.test(String(value).trim())) {
      return 'Only numbers are allowed';
    }
    if (MOBILE_FIELDS.includes(name) && String(value).trim().length !== 10) {
      return 'Must be exactly 10 digits';
    }
  }

  if (DATE_FIELDS.includes(name)) {
    const str = String(value).trim();
    const match = str.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})$/);
    if (!match) {
      return 'Use format dd/mm/yyyy or dd/mm/yy';
    }
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return 'Invalid date value';
    }
  }

  return '';
};

const StudentForm = ({ initialData = {}, onSave, onClose, isEditMode = false, columns = [], isSaving = false, onActivateToggle, onPay }) => {
  // Initialize form data with empty values for all columns
  const [formData, setFormData] = useState(
    columns.reduce((acc, column) => {
      acc[column] = initialData[column] ?? (column === 'Active' ? true : '');
      return acc;
    }, {})
  );
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numberFields = ['Rno', 'Default Discount'];

    // Validate and set error
    const error = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              numberFields.includes(name) ? (value === '' ? '' : Number(value)) :
              value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create a copy of formData with empty strings converted to null
    const dataToSave = Object.keys(formData).reduce((acc, key) => {
      acc[key] = formData[key] === '' ? null : formData[key];
      return acc;
    }, {});
    onSave(dataToSave);
  };

  // Get required fields from columns that have required: true in their config
  const requiredFields = useMemo(() => 
    columns.filter(col => getFieldConfig(col).required),
    [columns]
  );

  // Check if all required fields are filled AND no validation errors
  const hasErrors = useMemo(() => 
    Object.values(fieldErrors).some(err => err !== ''),
    [fieldErrors]
  );

  const isFormValid = useMemo(() => 
    !hasErrors && requiredFields.every(field => {
      const value = formData[field];
      return value !== undefined && value !== null && String(value).trim() !== '';
    }),
    [requiredFields, formData, hasErrors]
  );

  // Get visible fields based on edit mode
  const visibleFields = useMemo(
    () => getVisibleFields(columns, isEditMode),
    [columns, isEditMode]
  );

  // Render form field based on type
  const renderField = (fieldName) => {
    const config = getFieldConfig(fieldName);
    const value = formData[fieldName] ?? '';

    const commonProps = {
      name: fieldName,
      value: value,
      onChange: handleChange,
      placeholder: `Enter ${config.label || fieldName}`,
      required: config.required,
      disabled: config.disabled
    };

    switch (config.type) {
      case 'select':
        return (
          <div className="form-group" key={fieldName}>
            <label>
              {config.label || fieldName}
              {config.required && <span className="required"> *</span>}
            </label>
            <select {...commonProps}>
              {config.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option || `Select ${config.label || fieldName}`}
                </option>
              ))}
            </select>
          </div>
        );

      case 'textarea':
        return (
          <div className="form-group full-width" key={fieldName}>
            <label>
              {config.label || fieldName}
              {config.required && <span className="required"> *</span>}
            </label>
            <textarea
              {...commonProps}
              rows="3"
            />
          </div>
        );

      case 'checkbox':
        return (
          <div className="form-group checkbox" key={fieldName}>
            <label>
              {config.label || fieldName}
            </label>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: fieldName,
                    value: e.target.checked,
                    type: 'checkbox',
                    checked: e.target.checked
                  }
                });
              }}
              disabled={config.disabled}
            />
          </div>
        );

      default:
        return (
          <div className={`form-group ${fieldErrors[fieldName] ? 'has-error' : ''}`} key={fieldName}>
            <label>
              {config.label || fieldName}
              {config.required && <span className="required"> *</span>}
            </label>
            <input
              type={config.type || 'text'}
              {...commonProps}
            />
            {fieldErrors[fieldName] && (
              <span className="field-error">{fieldErrors[fieldName]}</span>
            )}
          </div>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="student-form">
      <div className="form-content">
        <div className="form-grid">
          {visibleFields.map(field => renderField(field))}
        </div>
      </div>

      <div className="student-form__actions">
        {isEditMode && onActivateToggle && (
          <button
            type="button"
            className={`student-form__toggle ${formData.Active ? 'student-form__toggle--deactivate' : 'student-form__toggle--activate'}`}
            onClick={() => onActivateToggle(formData)}
            disabled={isSaving}
          >
            {formData.Active ? 'Deactivate' : 'Activate'}
          </button>
        )}
        {isEditMode && onPay && (
          <button
            type="button"
            className="student-form__pay"
            onClick={() => onPay(formData)}
            disabled={isSaving}
          >
            Pay
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="student-form__cancel"
          onClick={onClose}
          disabled={isSaving}
        >
          {isSaving ? 'Please wait...' : 'Cancel'}
        </button>
        <button
          type="submit"
          className="student-form__submit"
          disabled={!isFormValid || isSaving}
        >
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              <span style={{ marginLeft: '8px' }}>
                {isEditMode ? 'Updating...' : 'Adding...'}
              </span>
            </>
          ) : (
            <>{isEditMode ? 'Update' : 'Add'} Student</>
          )}
        </button>
      </div>
    </form>
  );
};

export default StudentForm;
